import gql from 'graphql-tag';

import GraphQLClient from '../../adapter/GraphQLClient';
import HttpClient from '../../adapter/HttpClient';
import Language from '../../entity/Language';
import TimeZone from '../../entity/TimeZone';
import User from '../../entity/User';
import { Provider } from '../../entity/User';

class UserWebApi {
  private graphqlClient: GraphQLClient;
  private httpClient: HttpClient;

  constructor({ idGraphqlClient, httpClient }: { idGraphqlClient: GraphQLClient; httpClient: HttpClient }) {
    this.graphqlClient = idGraphqlClient;
    this.httpClient = httpClient;
  }

  public getCurrentUser = async (): Promise<User> => {
    const result = await this.graphqlClient.query(
      gql`
        query currentUser {
          currentUser {
            id
            name
            profileImageUrl
            email
            comment
            hasPassword
            providers
            createdAt
            allowPublicProfile
            allowPublicSearch
            receivingEmails
            comment
            lang
            timeZone
            unconfirmedEmail
            otpEnabled
            mfaRequired
            useUsFlow
            doneUrl
          }
        }
      `,
    );
    return User.fromJSON(result.currentUser) as User;
  };

  public changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation UpdateUserPassword($currentPassword: String!, $password: String!) {
          updateUserPassword(input: { password: $password, currentPassword: $currentPassword }) {
            clientMutationId
          }
        }
      `,
      { currentPassword, password: newPassword },
    );
  };

  public diconnectProvider = async (provider: Provider) => {
    await this.graphqlClient.query(
      gql`
        mutation deleteOauthProvider($provider: OauthProviderEnum!) {
          deleteOauthProvider(input: { provider: $provider }) {
            clientMutationId
          }
        }
      `,
      { provider: Provider[provider] },
    );
  };

  public changeEmail = async (email: string): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation createChangeUserEmailToken($email: String!) {
          createChangeUserEmailToken(input: { email: $email }) {
            clientMutationId
          }
        }
      `,
      { email },
    );
  };

  public resetChangeEmail = async (): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation deleteChangeUserEmailToken {
          deleteChangeUserEmailToken
        }
      `,
    );
  };

  public deleteUser = async (): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation deleteUser {
          deleteUser
        }
      `,
    );
  };

  public updateGeneral = async ({
    allowPublicProfile,
    allowPublicSearch,
    receivingEmails,
    comment,
    lang,
    timeZone,
    name,
  }: {
    allowPublicProfile: boolean;
    allowPublicSearch: boolean;
    receivingEmails: boolean;
    comment?: string;
    lang: Language;
    timeZone: TimeZone;
    name: string;
  }): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation UpdateUser(
          $allowPublicProfile: Boolean!
          $allowPublicSearch: Boolean!
          $receivingEmails: Boolean!
          $comment: String
          $lang: LangEnum!
          $timeZone: String!
          $name: String!
        ) {
          updateUser(
            input: {
              allowPublicProfile: $allowPublicProfile
              allowPublicSearch: $allowPublicSearch
              receivingEmails: $receivingEmails
              lang: $lang
              timeZone: $timeZone
              name: $name
              comment: $comment
            }
          ) {
            user {
              id
            }
          }
        }
      `,
      { allowPublicProfile, allowPublicSearch, receivingEmails, comment: comment || null, lang, timeZone, name },
    );
  };

  public createOtpSecretKey = async (): Promise<{ otpSecretKey: string; provisioningUri: string }> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation createUserOtpSecretKey {
          createUserOtpSecretKey {
            otpSecretKey
            provisioningUri
          }
        }
      `,
    );
    return {
      otpSecretKey: result.createUserOtpSecretKey.otpSecretKey,
      provisioningUri: result.createUserOtpSecretKey.provisioningUri,
    };
  };

  public enableOtp = async (otpCode: string): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation updateUserOtpEnabled($otpCode: String) {
          updateUserOtpEnabled(input: { otpEnabled: true, otpCode: $otpCode }) {
            clientMutationId
          }
        }
      `,
      { otpCode },
    );
  };

  public disableOtp = async (): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation updateUserOtpEnabled {
          updateUserOtpEnabled(input: { otpEnabled: false }) {
            clientMutationId
          }
        }
      `,
    );
  };

  public uploadAvatarImage = async (file: File): Promise<void> => {
    const formData = new FormData();

    formData.append('user[avatar_image]', file);

    await this.httpClient.post('/avatar_image', formData);
  };

  public updateUserLang = async (lang: Language): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation UpdateUserLang($lang: LangEnum!) {
          updateUserLang(input: { lang: $lang }) {
            user {
              id
            }
          }
        }
      `,
      { lang },
    );
  };
}

export default UserWebApi;
